import { computed, ref, watch } from 'vue';
import { defineStore, acceptHMRUpdate } from 'pinia';
import { useRouter, useRoute } from 'vue-router';
import { useToastStore } from '@/stores/toast';
import { useUserStore } from './user';
import {
  getDemoEventAnalytics,
  getEventById,
  getEventTemplateById,
  getReportedEvents,
} from '@/utils/api';
import { filterAvailableWidgets } from '@/utils/widgetFilters';

import type { ReportableEvent } from '@/types/Analytics';
import type { EventType } from '@/types/Event';
import type { WidgetObject, WidgetObjectSorted, WidgetOptions } from '@/types/Widget';

export const useAnalyticsStore = defineStore('analytics', () => {
  const router = useRouter();
  const route = useRoute();
  const userStore = useUserStore();

  const loading = ref(false);

  // Selected Event from Query Params
  const chosenEventId = ref<number | null>(null);

  // Possible Events
  const reportableEvents = ref<ReportableEvent[]>([]);

  // Selected Event
  const chosenEvent = ref<ReportableEvent>();

  // Details (widgets etc) for Selected Event
  const chosenEventDetails = ref<EventType>();

  // Widgets by Event + Template
  const chosenWidgets = ref<WidgetObjectSorted[]>([]);

  const isDemoEvent = ref(false);
  const demoEventData = ref();
  const demoEventTemplate = ref();

  const isBenchmarking = ref(true);

  const toast = useToastStore();
  const getReportableEvents = async (silent = false) => {
    if (!silent) loading.value = true;

    try {
      const resp = await getReportedEvents(userStore.accessToken);
      const demoEventOption =
        (reportableEvents.value.length === 1 && reportableEvents.value[0].id === demoEventData.value?.id)
          ? reportableEvents.value[0] : null;
      reportableEvents.value = resp;
      if (demoEventOption && !reportableEvents.value.some(event => event.id === demoEventOption.id)) {
        reportableEvents.value.unshift(demoEventOption);
      }

      if (!silent) {
        if (chosenEventId.value) {
          findEvent();
        } else if (
          !userStore.userData?.vantage_tour_completed &&
          userStore.demoEvent &&
          !isDemoEvent.value
        ) {
          await getDemoEventData();
          useDemoEvent();
          isDemoEvent.value = true;
        }
      }
    } finally {
      if (!silent) {
        loading.value = false;
      }
    }
  };

  const useDemoEvent = () => {
    const demoEventOption = {
      id: demoEventData.value.id,
      uuid: demoEventData.value.uuid,
      name: demoEventData.value.name,
    };

    if (!reportableEvents.value.length) {
      reportableEvents.value = [demoEventOption];
    } else if (!reportableEvents.value.some(event => event.id == demoEventOption.id)) {
      reportableEvents.value.unshift(demoEventOption);
    }

    chosenEvent.value = reportableEvents.value.find((option) => option.id === demoEventOption.id);

    const orderedWidgets = demoEventTemplate.value.widgets.map(
      (widget: WidgetObject, index: number) => {
        const positionedWidget = ref({
          id: widget.id,
          x: widget.position.x,
          y: widget.position.y,
          h: widget.position.h,
          w: widget.position.w,
          i: index,
          widget: widget,
        });
        return positionedWidget.value;
      },
    );

    chosenWidgets.value = orderedWidgets;
  };

  const getEventData = async (silent = false) => {
    if (!demoEventData.value) {
      demoEventData.value = userStore.demoEvent ? userStore.demoEvent['CampaignsEvent'] : null;
      demoEventTemplate.value = userStore.demoTemplate ? userStore.demoTemplate : null;
    }
    if (chosenEvent.value) {
      if (!demoEventData.value || chosenEvent.value.id !== demoEventData.value.id) {
        if (!silent) loading.value = true;
        const eventId = chosenEvent.value.id;

        await getEventById(userStore.accessToken, eventId).then((resp) => {
          loading.value = false;
          chosenEventDetails.value = resp.CampaignsEvent;
          getEventWidgets(silent);
        });
      } else {
        useDemoEvent();
        loading.value = false;
      }
    } else {
      loading.value = false;
    }
  };

  const getDemoEventData = async () => {
    if (!demoEventData.value) {
      demoEventData.value = userStore.demoEvent ? userStore.demoEvent['CampaignsEvent'] : null;
      demoEventTemplate.value = userStore.demoTemplate ? userStore.demoTemplate : null;
    }
    if (!demoEventData.value || !demoEventTemplate.value) {
      await getDemoEventAnalytics(userStore.accessToken).then((resp) => {
        const { data } = resp;
        demoEventData.value = data.event['CampaignsEvent'];
        demoEventTemplate.value = data.template;
      });
    }
  };

  const isDemoEventSelected = computed(() => {
    return demoEventData.value?.id === chosenEvent.value?.id;
  });

  watch(
    () => chosenEventId.value,
    () => {
      updateParams();
    },
    { immediate: true }
  );

  const getEventWidgets = async (silent = false) => {
    const template_id = chosenEventDetails.value?.template_id;
    if (chosenEvent.value && template_id) {
      if (!silent) loading.value = true;

      await getEventTemplateById(userStore.accessToken, chosenEvent.value.id, template_id).then(
        (resp) => {
          if (resp?.success === false) {
            toast.error(resp);
            chosenWidgets.value = [];
          } else {
            const orderedWidgets = filterAvailableWidgets(resp.widgets, userStore.hasFeature).map(
              (widget: WidgetOptions, index: number) => {
                // Sort Zone Options alphabetically
                if (widget.filters.area?.length) {
                  const zone = widget.filters.area.find((area) => area.name === 'Zone');
                  zone?.data?.sort((a, b) =>
                    (a.title?.toLowerCase() ?? '').localeCompare(b.title?.toLowerCase() ?? ''),
                  );
                }

                const positionedWidget = ref({
                  id: widget.id,
                  x: widget.position.x,
                  y: widget.position.y,
                  h: widget.position.h,
                  w: widget.position.w,
                  i: index,
                  widget: widget,
                });
                return positionedWidget.value;
              },
            );
            chosenWidgets.value = orderedWidgets;
          }
          if (route.path.includes('benchmarking') || silent) return; // Don't hide loading when benchmarking - there's still other loading going on
          loading.value = false;
        },
      );
    }
  };

  getReportableEvents();

  // 60,000 milliseconds to 1 minute x 30 minutes;
  const timer = 60000 * 30;
  const isSilent = true;
  setInterval(() => {
    getReportableEvents(isSilent);
  }, timer);

  function updateParams() {
    if (chosenEventId.value) {
      const currentId = route.query.eventId ? parseInt(`${route.query.eventId}`) : null;
      if (currentId !== chosenEventId.value) {
        if (route.query.eventId) {
          router.push({
            query: { eventId: chosenEventId.value },
          });
        } else {
          router.replace({
            query: { ...route.query, eventId: chosenEventId.value },
          });
        }
      }
    }
  }

  const findEvent = () => {
    if (reportableEvents.value.length) {
      const matched = reportableEvents.value.find((evt) => evt.id === chosenEventId.value);
      if (matched && chosenEvent.value?.id !== chosenEventId.value) {
        chosenEvent.value = matched;
        getEventData();
      }
    }
  };

  watch(
    () => route.query.eventId,
    (newId) => {
      if (newId) {
        chosenEventId.value = parseInt(`${newId}`);
        findEvent();
      }
    },
    { immediate: true },
  );

  const eventHasFeature = (feature: string, event = chosenEvent.value): boolean => {
    return !!event?.features?.some((f) => f.name === feature);
  };

  return {
    chosenEvent,
    chosenEventId,
    chosenEventDetails,
    chosenWidgets,

    isDemoEventSelected,
    isDemoEvent,
    getDemoEventData,
    useDemoEvent,
    updateParams,

    loading,
    isBenchmarking,

    reportableEvents,
    getReportableEvents,

    getEventWidgets,
    eventHasFeature,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAnalyticsStore, import.meta.hot));
}
